/**
 * Market Hours Utility
 * Provides functions to check NYSE market status
 *
 * Uses cached market status from API to properly handle holidays.
 */

import { apiService } from '../services/api';

// Cache for market status (fetched from API)
interface MarketStatusCache {
  date: string;
  market_status: 'open' | 'closed' | 'early_close';
  is_trading_day: boolean;
  holiday_name: string | null;
  open_time: string | null;
  close_time: string | null;
  last_trading_day: string | null;
  fetched_at: number;
}

let marketStatusCache: MarketStatusCache | null = null;

/**
 * Initialize market status by fetching from API.
 * Should be called on app initialization.
 * Cache is valid for 1 hour.
 */
export const initMarketStatus = async (): Promise<void> => {
  try {
    // Check if cache is still valid (less than 1 hour old)
    if (marketStatusCache && Date.now() - marketStatusCache.fetched_at < 60 * 60 * 1000) {
      return;
    }

    const response = await apiService.getMarketStatus();
    if (response.status === 'success') {
      marketStatusCache = {
        date: response.date,
        market_status: response.market_status,
        is_trading_day: response.is_trading_day,
        holiday_name: response.holiday_name,
        open_time: response.open_time,
        close_time: response.close_time,
        last_trading_day: response.last_trading_day,
        fetched_at: Date.now()
      };
    }
  } catch (error) {
    console.warn('Failed to fetch market status, falling back to basic logic:', error);
    // Cache will remain null, isMarketOpen will use fallback logic
  }
};

/**
 * Get the cached market status.
 * Returns null if not yet fetched.
 */
export const getMarketStatus = (): MarketStatusCache | null => {
  return marketStatusCache;
};

/**
 * Check if NYSE is currently open
 * Market hours: 9:30 AM - 4:00 PM ET, Monday-Friday (excluding holidays)
 *
 * Uses cached market status from API to properly handle holidays.
 * Falls back to basic day/time check if cache not available.
 */
export const isMarketOpen = (): boolean => {
  const now = new Date();
  const nyTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const day = nyTime.getDay();
  const hour = nyTime.getHours();
  const minute = nyTime.getMinutes();
  const timeInMinutes = hour * 60 + minute;

  // Default market hours
  let marketOpen = 9 * 60 + 30;  // 9:30 AM
  let marketClose = 16 * 60;     // 4:00 PM

  // Check if weekend
  if (day === 0 || day === 6) {
    return false;
  }

  // Check cached market status for holidays and early close
  if (marketStatusCache) {
    // Today is marked as closed (holiday or other)
    if (marketStatusCache.market_status === 'closed') {
      return false;
    }

    // Early close day - adjust close time
    if (marketStatusCache.market_status === 'early_close' && marketStatusCache.close_time) {
      const [closeHour, closeMinute] = marketStatusCache.close_time.split(':').map(Number);
      marketClose = closeHour * 60 + closeMinute;
    }
  }

  // Check if within market hours
  return timeInMinutes >= marketOpen && timeInMinutes < marketClose;
};

/**
 * Check if today is a holiday (market closed all day)
 */
export const isMarketHoliday = (): boolean => {
  if (!marketStatusCache) {
    return false;
  }
  return marketStatusCache.market_status === 'closed' && marketStatusCache.holiday_name !== null;
};

/**
 * Get holiday name if today is a holiday
 */
export const getHolidayName = (): string | null => {
  if (!marketStatusCache) {
    return null;
  }
  return marketStatusCache.holiday_name;
};

/**
 * True when today has no regular trading session at all (weekend or full-day
 * holiday), per the API-backed market calendar. Used to show a "Closed" state
 * instead of a 0% / N/A daily change when the market never opened.
 *
 * Returns false when status is unknown (cache not yet fetched) so we never
 * wrongly assert "closed" — callers fall back to showing the change value.
 */
export const isNonTradingDay = (): boolean => {
  if (!marketStatusCache) {
    return false;
  }
  return marketStatusCache.is_trading_day === false;
};

/**
 * Check if we are currently in pre-market hours.
 * Pre-market: 4:00 AM - 9:29 AM ET, Monday-Friday (excluding holidays)
 *
 * During pre-market, Polygon snapshot API returns data via the minute bar
 * (min.c) since the regular day session hasn't started yet.
 */
export const isPreMarket = (): boolean => {
  const now = new Date();
  const nyTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const day = nyTime.getDay();
  const hour = nyTime.getHours();
  const minute = nyTime.getMinutes();
  const timeInMinutes = hour * 60 + minute;

  // Not pre-market on weekends
  if (day === 0 || day === 6) {
    return false;
  }

  // Check cached market status for holidays
  if (marketStatusCache && marketStatusCache.market_status === 'closed') {
    return false;
  }

  // Pre-market: 4:00 AM (240 min) to 9:29 AM (569 min) ET
  const preMarketOpen = 4 * 60;      // 4:00 AM
  const marketOpen = 9 * 60 + 30;    // 9:30 AM

  return timeInMinutes >= preMarketOpen && timeInMinutes < marketOpen;
};

/**
 * Get the last trading day date string (YYYY-MM-DD format).
 * Useful for showing which day's data is being displayed when market is closed.
 */
export const getLastTradingDay = (): string | null => {
  if (!marketStatusCache) {
    return null;
  }
  return marketStatusCache.last_trading_day;
};
